import { styled } from '@mui/material';
import useSettingsNavigation from 'hooks/settings/useSettingsNavigation';
import { useEffect, useMemo, useRef } from 'react';
import { Button, Icon, Stack, Text } from 'shell/components';
import { useDraggableScroll } from 'shell/hooks';

const VERTICAL_NAV_WIDTH = Object.freeze({ desktop: 331, tablet: 231 });
export const SETTINGS_NAVIGATION_VARIANT = Object.freeze({
    HORIZONTAL: 'horizontal',
    VERTICAL: 'vertical',
});

const focusVisibleStyles = (theme) => ({
    outline: `3px solid ${theme.palette.ensightful.secondary[400]}`,
    outlineOffset: 2,
});

const SettingsNavigation = ({ groups, labels, variant = SETTINGS_NAVIGATION_VARIANT.VERTICAL, ariaLabel }) => {
    const sections = useMemo(() => groups.flatMap(({ sections: groupSections }) => groupSections), [groups]);
    const sectionIds = useMemo(() => sections.map(({ id }) => id), [sections]);
    const { activeId, scrollTo } = useSettingsNavigation(sectionIds);

    if (variant === SETTINGS_NAVIGATION_VARIANT.HORIZONTAL) {
        return (
            <HorizontalSettingsNavigation
                activeId={activeId}
                ariaLabel={ariaLabel}
                groups={groups}
                labels={labels}
                scrollTo={scrollTo}
            />
        );
    }

    return (
        <VerticalSettingsNavigation
            activeId={activeId}
            ariaLabel={ariaLabel}
            groups={groups}
            labels={labels}
            scrollTo={scrollTo}
        />
    );
};

const HorizontalSettingsNavigation = ({ activeId, ariaLabel, groups, labels, scrollTo }) => {
    const buttonRefs = useRef({});
    const { ref: horizontalNavRef, events: horizontalNavEvents, isDragging } = useDraggableScroll();

    useEffect(() => {
        const button = buttonRefs.current[activeId];
        const container = horizontalNavRef.current;
        if (!button || !container) return;
        const buttonRect = button.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const target =
            container.scrollLeft +
            buttonRect.left -
            containerRect.left -
            (container.clientWidth - buttonRect.width) / 2;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollTo({ left: Math.max(0, Math.min(target, maxScrollLeft)), behavior: 'smooth' });
    }, [activeId, horizontalNavRef]);

    return (
        <HorizontalNav ref={horizontalNavRef} aria-label={ariaLabel} $isDragging={isDragging} {...horizontalNavEvents}>
            {groups.map(({ id, sections }) => (
                <HorizontalGroup key={id}>
                    {sections.map((section) => (
                        <MobileButton
                            key={section.id}
                            ref={(element) => {
                                buttonRefs.current[section.id] = element;
                            }}
                            aria-current={activeId === section.id ? 'location' : undefined}
                            $active={activeId === section.id}
                            type='button'
                            onClick={() => scrollTo(section.id)}
                        >
                            {labels[section.id]}
                        </MobileButton>
                    ))}
                </HorizontalGroup>
            ))}
        </HorizontalNav>
    );
};

const VerticalSettingsNavigation = ({ activeId, ariaLabel, groups, labels, scrollTo }) => (
    <VerticalNav aria-label={ariaLabel}>
        {groups.map(({ id, icon, sections }) => (
            <NavGroup key={id}>
                <CategoryLabel type='button' onClick={() => scrollTo(sections[0]?.id)}>
                    <Icon kind={icon} sx={{ fontSize: 24 }} />
                    <Text variant='p1' weight='semibold'>
                        {labels[id]}
                    </Text>
                </CategoryLabel>
                {sections.map((section) => (
                    <NavButton
                        key={section.id}
                        aria-current={activeId === section.id ? 'location' : undefined}
                        $active={activeId === section.id}
                        type='button'
                        variant='borderless'
                        onClick={() => scrollTo(section.id)}
                    >
                        {labels[section.id]}
                    </NavButton>
                ))}
            </NavGroup>
        ))}
    </VerticalNav>
);

const VerticalNav = styled('nav')(({ theme }) => ({
    alignSelf: 'flex-start',
    flex: `0 0 ${VERTICAL_NAV_WIDTH.desktop}px`,
    position: 'sticky',
    top: theme.spacing(2),
    width: VERTICAL_NAV_WIDTH.desktop,

    [theme.breakpoints.down('desktop')]: {
        flexBasis: VERTICAL_NAV_WIDTH.tablet,
        width: VERTICAL_NAV_WIDTH.tablet,
    },
}));

const NavGroup = styled(Stack)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    '&:first-child': { paddingTop: 12 },
}));

const CategoryLabel = styled('button')(({ theme }) => ({
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    display: 'flex',
    font: 'inherit',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    textAlign: 'left',
    width: '100%',

    '&:focus-visible': focusVisibleStyles(theme),
}));
const NavButton = styled(Button)(({ theme, $active }) => ({
    '&.MuiButton-root': {
        alignSelf: 'stretch',
        background: $active ? theme.palette.ensightful.primary[50] : 'transparent',
        borderRadius: 6,
        color: theme.palette.ensightful.neutral[900],
        fontSize: 16,
        fontWeight: 400,
        justifyContent: 'flex-start',
        marginLeft: theme.spacing(4),
        marginRight: theme.spacing(1.5),
        minHeight: 'unset',
        padding: theme.spacing(1.5, 2),
        textAlign: 'left',
    },
}));

const HorizontalNav = styled('nav', { shouldForwardProp: (prop) => prop !== '$isDragging' })(
    ({ theme, $isDragging }) => ({
        alignItems: 'center',
        background: theme.palette.ensightful.shades[0],
        display: 'flex',
        gap: theme.spacing(3),
        maxWidth: '100%',
        overflowX: 'auto',
        padding: theme.spacing(1.5, 2),
        position: 'sticky',
        top: 0,
        whiteSpace: 'nowrap',
        zIndex: 2,
        scrollbarWidth: 'none',
        cursor: $isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
    })
);

const HorizontalGroup = styled(Stack)(({ theme }) => ({ flexDirection: 'row', gap: theme.spacing(1) }));

const MobileButton = styled('button')(({ theme, $active }) => ({
    background: $active ? theme.palette.ensightful.primary[50] : 'transparent',
    border: 0,
    borderRadius: 6,
    color: theme.palette.ensightful.neutral[900],
    cursor: 'pointer',
    flexShrink: 0,
    font: 'inherit',
    fontSize: 16,
    padding: theme.spacing(1.5, 2),

    '&:focus-visible': focusVisibleStyles(theme),
}));

export default SettingsNavigation;
